import { RequestPacket, ResponsePacket, createPortPair, findTransferables, unwrap, wrap, RpcWrapper, RPCTarget, reportErrorGlobally } from "./shared";


const __globalId = (() => {
    let id = 0;
    return () => id++;
})();
export class RPCClient {
    private globalId: number;
    private id: number = 0;

    private port: RPCTarget;
    // private waiting = new Map<string, (response: MessageEvent<ResponsePacket>) => void>();

    constructor(port: RPCTarget) {
        this.port = port;
        this.globalId = __globalId();
    }

    call<T>(name: string, params: any[], abort?: AbortSignal): Promise<T> {
        const ownId = `${this.globalId}--${this.id++}`;

        return new Promise((rs, rj) => {
            const closer = () => {
                abort?.removeEventListener("abort", closer);
                this.port.removeEventListener("message", listener);
            };
            const listener = (msg: MessageEvent<ResponsePacket>) => {
                if (msg.data.id !== ownId) return;

                if (msg.data.type == "error") {
                    rj(new Error(msg.data.reason));
                } else {
                    try {
                        rs(unwrap(msg.data.data));
                    } catch (e) {
                        rj(e);
                    }
                }
                closer();
            };
            
            abort?.addEventListener("abort", closer);
            this.port.addEventListener("message", listener);

            const ps = params.map(p => wrap(p));
            const transferablePs = ps.map(({post: _, ...extra}) => extra);

            const packet = {
                type: "call",
                id: ownId,
                name,
                params: transferablePs
            } as RequestPacket;

            if (process.env.NODE_ENV === 'development') {
                console.debug("[RPC::Client] calling", packet);
            }

            this.port!.postMessage(packet, findTransferables(transferablePs));
            ps.map(p => p.post?.call(p));
        });
    }

    close() {
        this.port?.postMessage({type: "close"});
        this.port.close?.call(this.port);
    }

    rpcWrap(): RpcWrapper {
        const [us, you] = createPortPair();
        us.addEventListener("message", (msg: MessageEvent<RequestPacket>) => {
            if (msg.data.type === "close") {
                us.close();
                return;
            }
            (async () => {
                if (msg.data.type === "close") return;

                try {
                    const result = wrap(await this.call(
                        msg.data.name,
                        msg.data.params.map(p => unwrap(p))
                    ));
                    us.postMessage({
                        "type": "result",
                        "data": {...result, post: undefined},
                        "id": msg.data.id
                    } as ResponsePacket);
                    result.post?.call(result.post);
                } catch (e: any) {
                    us.postMessage({
                        "type": "error",
                        "reason": e.toString(),
                        id: msg.data.id
                    } as ResponsePacket);
                }
            })().catch(reportErrorGlobally);
        });
        us.start();
        return {
            type: "proxy",
            value: you,
            post: () => you.close()
        }
    }
    build<P extends RPCObject>(p: (b: ProxyBuilder<P>) => void): P {
        const self = this;
        const obj: Partial<P> = {};

        const builder: ProxyBuilder<P> = {
            declare(name) {
                obj[name] = (async (...params: Parameters<P[typeof name]>) => {
                    return await self.call(name, params);
                }) as any;
                return builder;
            }
        };
        p(builder);

        return obj as any as P;
    }
}


type RPCObject = Record<string, (...params: any) => Promise<any>>;
interface ProxyBuilder<T extends RPCObject> {
    declare<N extends keyof T>(name: N & string): ProxyBuilder<T>;
}

